<template>
  <div class="flex flex-1 min-h-0 items-stretch">
    <div
      class="p-2 pr-0 border-r border-gray-300 flex flex-col"
      style="width: 22rem"
    >
      <BaseTabs
        v-model="activeMessageTab"
        :tabs="['Skickade', 'Återkallade']"
        class="mb-3"
        tabs-class="flex-1"
      />
      <div
        class="flex flex-col flex-1 min-h-0  divide-y-1 divide-gray-300 overflow-y-auto"
      >
        <p v-if="loadingMessages" class="mx-auto text-gray-700">
          Hämtar meddelanden...
        </p>
        <p v-else-if="messages.length == 0" class="mx-auto text-gray-700">
          Inga meddelanden hittades
        </p>
        <template v-else>
          <div
            v-for="(message, index) of messages"
            :key="index"
            class="p-2 pr-8 cursor-pointer hover:bg-lagon-50 hover:bg-opacity-50 flex justify-between select-none relative"
            :class="
              openedMessage.id == message.id
                ? 'bg-lagon-50  text-lagon-800'
                : 'text-gray-900'
            "
            @click="openMessage(index)"
          >
            <span
              class="font-semibold truncate mr-2"
              :style="{
                maxWidth: '15rem',
                ...(message.isBanner && {
                  borderBottomWidth: '4px',
                  borderBottomColor: getBannerColor(message.bannerLevel),
                }),
              }"
              :title="message.bannerMessage || message.title"
              >{{ message.bannerMessage || message.title }}</span
            >
            <span class="flex-none">{{
              moment(message.createdAt).format('YYYY-MM-DD')
            }}</span>
            <svg
              v-if="openedMessage.id == message.id"
              class="absolute fill-current text-lagon-100 opacity-50 right-0  cursor-default"
              viewBox="0 0 24 24"
              height="24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6,0 L20,12 L6,24" />
            </svg>
          </div>
        </template>
      </div>
    </div>
    <div
      v-if="messages.length > 0"
      class="border-r border-gray-300 flex flex-col py-2"
      style="width: 23rem;"
    >
      <BaseTabs
        v-model="activeRecipientTab"
        :tabs="['Mottagare', 'Har läst', 'Har ej läst']"
        tabs-class="whitespace-no-wrap flex-1"
        class="mb-3"
      />
      <div class="overflow-y-auto min-h-0 flex flex-1 flex-col px-6">
        <p v-if="!openedMessage" class="text-center text-gray-700">
          Inget meddelande valt
        </p>
        <div v-else-if="activeRecipientTab == 'Mottagare' && openedMessage">
          <p class="mb-3 text-center">
            Skickat till
            <span
              >{{ openedMessage.recipients.length }}
              {{
                openedMessage.recipients.length == 1 ? 'salong' : 'salonger'
              }}</span
            >
          </p>
          <div
            v-if="
              openedMessage.filtersUsed && openedMessage.filtersUsed.length > 0
            "
            class="flex flex-wrap gap-2 mb-3"
          >
            <p
              v-if="
                !openedMessage.filtersUsed ||
                  !openedMessage.filtersUsed.join('')
              "
              class="italic"
            >
              Inga filter användes
            </p>
            <template v-else>
              <p>
                Använda filter:
              </p>
              <div
                v-for="filter of openedMessage.filtersUsed"
                :key="filter"
                class="filter-item"
                style="cursor: default"
              >
                {{ filterTypes[filter] || filter }}
              </div>
            </template>
          </div>
        </div>
        <p v-else-if="loadingRecipients" class="text-center text-gray-700">
          Laddar...
        </p>
        <p v-else-if="recipients.length == 0" class="text-center text-gray-700">
          Inga salonger att visa
        </p>
        <template v-else>
          <div
            v-for="saloon of recipients"
            :key="saloon.id"
            class="flex justify-between items-baseline"
          >
            <div class="flex flex-wrap max-w-full min-w-0">
              <p class="font-semibold truncate mr-1">
                {{ saloon.company.displayname || saloon.company.name }}
              </p>
              <p class="truncate">- {{ saloon.name }}</p>
            </div>
            <span
              v-if="getDateRead(saloon.id)"
              class="text-sm text-lagon-500 flex-none"
              >{{ moment(getDateRead(saloon.id)).format('D MMM HH:mm') }}</span
            >
          </div>
        </template>
      </div>
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto text-gray-900">
      <div
        v-if="
          openedMessage && messages.map(m => m.id).includes(openedMessage.id)
        "
        class="h-full flex flex-col px-24"
      >
        <div class="mx-auto flex flex-col items-end mt-4 mb-12">
          <h2 class="font-light text-3xl">{{ openedMessage.title }}</h2>
          <span class="text-sm">{{
            moment(openedMessage.createdAt).format('ddd D MMMM YYYY, HH:mm')
          }}</span>
          <p
            v-if="openedMessage.deleted"
            class="font-semibold text-coral-500 w-full text-center"
          >
            (Borttaget meddelande)
          </p>
        </div>
        <p
          class="whitespace-pre-line pb-16"
          v-html="openedMessage.longMessage || openedMessage.shortMessage"
        />
        <div
          v-if="
            !openedMessage.deleted &&
              (openedMessage.isBanner || isLessThanOneDayOld(openedMessage))
          "
          class="pb-16 mx-auto mt-auto"
        >
          <button
            class="text-coral-500 hover:text-coral-300 p-2 transition-colors font-semibold text-base"
            @click="deleteMessage(openedMessage.id)"
          >
            Återkalla Meddelande
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import BaseTabs from '@/components/BaseTabs'
import filterTypes from './filterTypes'
import GET_SALOONS from '../../graphql/Saloons.gql'
import GET_MESSAGES from '../../graphql/VoadyMessages.gql'
import gql from 'graphql-tag'
import { getBannerColor } from './banner.helpers'

export default {
  name: 'Archive',
  components: {
    BaseTabs,
  },
  apollo: {
    messages: {
      client: 'voadyApi',
      query: GET_MESSAGES,
      variables() {
        return {
          deleted: this.activeMessageTab == 'Återkallade',
        }
      },
      update: data => data.allVoadyMessages || [],
      result({ data }) {
        this.openedMessage =
          data && data.allVoadyMessages.length ? data.allVoadyMessages[0] : null
      },
      fetchPolicy: 'cache-and-network',
      loadingKey: 'loadingMessages',
    },
    recipients: {
      query: GET_SALOONS,
      skip() {
        return (
          !this.openedMessage ||
          !this.openedMessage.recipients ||
          !this.openedMessage.recipients.length
        )
      },
      variables() {
        let saloonIds

        if (this.activeRecipientTab == 'Mottagare') {
          saloonIds = this.openedMessage.filtersUsed.includes('SELECTED')
            ? this.openedMessage.recipients.map(r => r.saloonId)
            : []
        } else {
          saloonIds = this.openedMessage.recipients
            .filter(r =>
              this.activeRecipientTab == 'Har läst' ? !!r.dateRead : !r.dateRead
            )
            .map(r => r.saloonId)
        }

        return { where: { saloonIds } }
      },
      update: data => data.saloons,
      loadingKey: 'loadingRecipients',
    },
  },
  data() {
    return {
      messages: [],
      openedMessage: null,
      recipients: [],
      activeMessageTab: 'Skickade',
      activeRecipientTab: 'Mottagare',
      loadingMessages: 0,
      loadingRecipients: 0,
      filterTypes,
    }
  },
  methods: {
    getBannerColor,
    moment,
    openMessage(index) {
      this.openedMessage = this.messages[index]
    },
    getDateRead(saloonId) {
      const targetRecipient =
        this.openedMessage && this.openedMessage.recipients
          ? this.openedMessage.recipients.find(r => r.saloonId == saloonId)
          : null

      return targetRecipient ? targetRecipient.dateRead : null
    },
    async deleteMessage(id) {
      const confirmation = await this.$swal({
        title: 'Vill du ta bort meddelandet?',
      })

      if (!confirmation.isConfirmed) return

      const {
        data: {
          deleteVoadyMessage: { success },
        },
      } = await this.$apollo.mutate({
        mutation: gql`
          mutation deleteVoadyMessage($id: String!) {
            deleteVoadyMessage(id: $id) {
              success
            }
          }
        `,
        client: 'voadyApi',
        variables: { id },
      })

      if (success) {
        this.$apollo.queries.messages.refetch()
        this.$swal({
          title: 'Meddelandet togs bort',
          showCancelButton: false,
          showConfirmButton: false,
          timer: 1000,
        })
      }
    },
    isLessThanOneDayOld(message) {
      return (
        new Date().getTime() - new Date(message.createdAt).getTime() < 86400000
      )
    },
  },
}
</script>
